import { Box, Typography, Button, TextField, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Homeimages } from "../../assets";
import BoxInput from "../../Components/BoxInput";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";


import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import { useMutation } from '@tanstack/react-query';
import { contactForm } from "../../api/services";

const schema: yup.ObjectSchema<Omit<LeadData, 'type'>> = yup.object({
  name: yup.string().required('Name is required'),
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  phoneNumber: yup
    .string()
    .matches(
      /^[6-9]\d{9}$/,
      'Phone number must start with 6, 7, 8, or 9 and be 10 digits',
    )
    .required('Phone number is required'),
  message: yup.string().optional(),
  source: yup.string().optional(),
});

// ✅ UPDATED: Added source field to interface
interface LeadData {
  name: string;
  email: string;
  phoneNumber: string;
  message?: string;
  type?: string;
  source?: string;
}


interface LuxuryPopupProps {
  open: boolean;
  onClose: () => void;
}

const LuxuryPopup = ({ open, onClose }: LuxuryPopupProps) => {
 
  // ✅ ALWAYS call hooks FIRST
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LeadData>({
    resolver: yupResolver(schema),
  });

  const mutation = useMutation({
    mutationFn: (newFormData: LeadData) => contactForm(newFormData),
    onSuccess: () => {
      toast.success('Successfully submitted!');
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Submission failed');
    },
  });

  const resetForm = () => {
    reset({
      name: '',
      email: '',
      phoneNumber: '',
      message: '',
    });
  };

  const onSubmit = (data: LeadData) => {
    mutation.mutate({ ...data, type: 'contact', source: 'Mandate webpage' });
  };

  // ✅ AFTER hooks → conditional render
  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(8px)",
      }}
    >
      {/* 💎 POPUP CARD */}
      <Box
        sx={{
          width: { xs: "90%", md: "420px" },
          px: 4,
          py: 4,
          borderRadius: "28px",
          position: "relative",
          background: "rgba(255,255,255,0.08)",
          backdropFilter: "blur(25px)",
          border: "1px solid rgba(255,255,255,0.2)",
          boxShadow: `
        0 30px 80px rgba(0,0,0,0.6),
        inset 0 1px 0 rgba(255,255,255,0.2)
      `,
          overflow: "hidden",
        }}
      >
        {/* 💎 WATERMARK */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${Homeimages.rwsignlogo})`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "220px",
            opacity: 0.06,
            filter: "grayscale(100%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* CLOSE BUTTON */}
        {/* <Box
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "#fff",
            cursor: "pointer",
            zIndex: 2,
          }}
        >
          <CloseIcon />
        </Box> */}

        {/* CONTENT */}
        <Box
          onSubmit={handleSubmit(onSubmit)}
          component="form"
          sx={{ position: "relative", zIndex: 2 }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: -10,   // 🔥 moves ABOVE content
              right: -10, // 🔥 perfect corner alignment

              width: 40,
              height: 40,

              background: "rgba(255,255,255,0.15)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.2)",
              color: "#fff",

              zIndex: 10, // 🔥 always on top

              transition: "0.3s",

              "&:hover": {
                background: "rgba(255,255,255,0.3)",
                transform: "rotate(90deg) scale(1.1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          {/* TITLE */}
          <Typography
            sx={{
              color: "#fff",
              fontSize: 22,
              mb: 4,
              fontFamily: "Playfair Display",
            }}
          >
            Request Consultation
          </Typography>

          {/* NAME */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <BoxInput
                  placeholder="Enter your full name"
                  {...field}
                  fullWidth

                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

          </Box>

          {/* EMAIL */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <BoxInput
                  placeholder="Enter your email address"
                  {...field}
                  fullWidth

                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />

          </Box>

          {/* PHONE */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <BoxInput
                  placeholder="Enter your phone number"
                  {...field}

                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message}
                />
              )}
            />

          </Box>

          {/* MESSAGE */}
          <Box sx={{ mb: 3 }}>
            <Controller
              name="message"
              control={control}
              render={({ field }) => (
                <BoxInput
                  placeholder="Enter your message"
                  {...field}
                  multiline
                  rows={3}
                  fullWidth

                />
              )}
            />

          </Box>

          {/* BUTTON */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button
              type="submit"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 5,
                py: 1.5,
                borderRadius: "40px",
                fontWeight: 600,
                textTransform: "none",
                background: "#ffffff",
                color: "#0f63a5",
                boxShadow: "0 15px 40px rgba(0,0,0,0.3)",

                "&:hover": {
                  background: "#f1f5f9",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Submit
            </Button>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default LuxuryPopup;